import { useEffect, useRef, useState } from "react";

import { useGetAccessEligibilityStatus } from "@/api/generated";
import type { AccessEligibilityMetadata } from "@/api/generated/models";
import { AccessEligibilityStatus } from "@/api/generated/models";
import {
  Button,
  Footer,
  Header,
  Heading,
  LoadingSpinner,
  Main,
  Paragraph,
  ScrollView,
  Text,
  YStack,
} from "@/components";
import { useUserProgression } from "@/conversation/sidebar/UserProgression/UserProgressionContext";

// A backstop for a dropped socket; a settled check normally arrives as a side effect.
const POLL_INTERVAL_MS = 5000;

export const CMSEligibilityScreen = ({
  onContinue,
}: {
  onContinue: (args?: { metadata?: AccessEligibilityMetadata }) => void;
}) => {
  const progressionId = useUserProgression()?.progression.id;
  const canCheck = Boolean(progressionId);
  const [hasStarted, setHasStarted] = useState(false);

  const { data, isError } = useGetAccessEligibilityStatus(
    { progression_id: progressionId ?? "" },
    {
      query: {
        enabled: canCheck && hasStarted,
        refetchOnWindowFocus: true,
        refetchInterval: (query) =>
          query.state.data?.status === AccessEligibilityStatus.in_progress
            ? POLL_INTERVAL_MS
            : false,
        meta: { suppressErrorToast: true },
      },
    },
  );

  const hasAdvancedRef = useRef(false);
  useEffect(() => {
    if (!hasStarted || hasAdvancedRef.current) {
      return;
    }
    const settled =
      data?.status === AccessEligibilityStatus.completed ||
      data?.status === AccessEligibilityStatus.failed;
    if (canCheck && !isError && !settled) {
      return;
    }
    hasAdvancedRef.current = true;
    onContinue({
      metadata: {
        type: "access_eligibility",
        access_eligible: Boolean(data?.result?.eligible),
      },
    });
  }, [
    data?.status,
    data?.result?.eligible,
    canCheck,
    hasStarted,
    isError,
    onContinue,
  ]);

  if (!hasStarted) {
    return (
      <>
        <ScrollView
          flex={1}
          contentContainerStyle={{
            paddingHorizontal: "$200",
            paddingBottom: "$400",
            gap: "$200",
          }}
        >
          <Header paddingTop="$100">
            <Heading size="$xl">Check your program eligibility</Heading>
          </Header>
          <Main>
            <Paragraph>
              This program is part of a new Medicare payment model. Your health
              information may be shared with CMS. You may be randomly selected
              into a comparison group and not get in for 12 months, through no
              fault of your own.
            </Paragraph>
          </Main>
        </ScrollView>
        <Footer mode="dialog" padding="$200">
          <Button
            dataTrackingId="access-start-eligibility-check-button"
            mode="primary"
            constrained
            onPress={() => setHasStarted(true)}
          >
            Check eligibility
          </Button>
        </Footer>
      </>
    );
  }

  return (
    <YStack
      flex={1}
      justifyContent="center"
      alignItems="center"
      gap="$100"
      paddingHorizontal="$200"
      paddingBottom="$200"
    >
      <LoadingSpinner size="$1000" />
      <Heading size="$md" textAlign="center">
        Checking your program eligibility
      </Heading>
      <Text color="$textSecondary" textAlign="center">
        We're confirming your Medicare coverage for this program.
      </Text>
    </YStack>
  );
};
